/**
 * Declarative sub-navigation for agent context (paths must match vue-router).
 * Primary/secondary sidebar items live in sidebarLinks.js; this file adds
 * nested items that are not in the main array (e.g. Contacts tabs, Settings).
 */

import { FEATURES } from '@/access/features';
import { PERMISSIONS } from '@/access/permissions';

/** @typedef {{ label: string, path: string, routeName?: string, feature?: string, permission?: string, roles?: string[], hidden?: boolean }} NavChild */

/** @type {NavChild[]} */
export const CONTACTS_SUBNAV = [
  {
    label: 'All Contacts',
    path: '/contacts',
    routeName: 'contacts-main',
  },
  {
    label: 'Views',
    path: '/contacts/views',
    routeName: 'contacts-views',
  },
  {
    label: 'Archived',
    path: '/contacts/archived',
    routeName: 'contacts-archived',
    feature: FEATURES.CONTACTS_ARCHIVED,
  },
  {
    label: 'Notes',
    path: '/contacts/notes',
    routeName: 'contacts-notes',
    feature: FEATURES.CONTACTS_NOTES,
  },
  {
    label: 'Tasks',
    path: '/contacts/tasks',
    routeName: 'contacts-tasks',
    feature: FEATURES.CONTACTS_TASKS,
  },
  {
    label: 'Relationships',
    path: '/contacts/relationships',
    routeName: 'contacts-relationships',
    feature: FEATURES.CONTACTS_RELATIONSHIPS,
  },
  {
    label: 'Boards',
    path: '/contacts/boards',
    routeName: 'contacts-boards',
    feature: FEATURES.CONTACTS_BOARDS,
  },
  {
    label: 'Import contacts',
    path: '/contacts/import',
    routeName: 'contacts-import',
  },
  {
    label: 'New contact',
    path: '/contacts/create',
    routeName: 'contact-create',
  },
];

/** @type {NavChild[]} */
export const GROUPS_SUBNAV = [
  {
    label: 'All groups',
    path: '/groups',
    routeName: 'groups',
  },
  {
    label: 'New group',
    path: '/groups/create',
    routeName: 'group-create',
  },
];

/**
 * Settings sidebar (matches Settings.vue navLinks + otherSettingLinks paths).
 * @type {NavChild[]}
 */
export const SETTINGS_SUBNAV = [
  {
    label: 'Profile',
    path: '/settings/profile',
    routeName: 'settings-profile',
    roles: ['admin', 'guest'],
  },
  {
    label: 'Users',
    path: '/settings/users',
    routeName: 'settings-users',
    feature: FEATURES.USERS,
    permission: PERMISSIONS.READ_USERS,
    roles: ['admin', 'guest'],
  },
  {
    label: 'Archived Users',
    path: '/settings/archived-users',
    routeName: 'settings-archived-users',
    feature: FEATURES.ARCHIVED_USERS,
    permission: PERMISSIONS.READ_USERS,
    roles: ['admin', 'guest'],
  },
  {
    label: 'User Permissions',
    path: '/settings/permissions',
    routeName: 'settings-permissions',
    permission: PERMISSIONS.UPDATE_USERS,
    feature: FEATURES.USER_PERMISSIONS,
    roles: ['admin', 'guest'],
  },
  {
    label: 'Teams',
    path: '/settings/teams',
    routeName: 'settings-teams',
    feature: FEATURES.TEAMS,
    roles: ['admin'],
  },
  {
    label: 'Tags',
    path: '/settings/tags',
    routeName: 'settings-tags',
    feature: FEATURES.TAGS,
    roles: ['admin'],
  },
  {
    label: 'Fields',
    path: '/settings/fields',
    routeName: 'settings-fields',
    feature: FEATURES.FIELDS,
    roles: ['admin'],
  },
  {
    label: 'Account',
    path: '/settings/account',
    routeName: 'settings-account',
    feature: FEATURES.ACCOUNT,
    roles: ['admin', 'guest'],
  },
  {
    label: 'Integrations',
    path: '/settings/integrations',
    routeName: 'settings-integrations',
    feature: FEATURES.INTEGRATIONS,
    roles: ['admin'],
  },
  {
    label: 'Portal Settings',
    path: '/settings/portal',
    routeName: 'settings-portal',
    roles: ['admin'],
  },
  {
    label: 'Links',
    path: '/settings/links',
    routeName: 'settings-links',
    permission: PERMISSIONS.LINKS,
    feature: FEATURES.LINKS,
    roles: ['admin', 'guest'],
  },
  {
    label: 'Backend',
    path: '/settings/backend',
    routeName: 'settings-backend',
    hidden: true,
    roles: ['admin'],
  },
  {
    label: 'Compliance',
    path: '/settings/compliance',
    routeName: 'settings-compliance',
    hidden: true,
    roles: ['admin'],
  },
  {
    label: 'Business Information',
    path: '/settings/business',
    routeName: 'settings-business',
    feature: FEATURES.OTHER_SETTINGS,
    roles: ['admin'],
  },
  {
    label: 'Email Templates',
    path: '/settings/email-templates',
    routeName: 'settings-email-templates',
    feature: FEATURES.EMAIL_TEMPLATES,
    roles: ['admin'],
  },
  {
    label: 'Email Addresses',
    path: '/settings/email-addresses',
    routeName: 'settings-email-addresses',
    feature: FEATURES.EMAIL_ADDRESSES,
    roles: ['admin'],
  },
  {
    label: 'Conversations',
    path: '/settings/conversations',
    routeName: 'settings-conversations',
    roles: ['admin'],
  },
];
